import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useSetting } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';

export default function HeroSection() {
  const [totalDomains, setTotalDomains] = useState(0);
  const [prizePool, setPrizePool] = useState(eventInfo.totalPrizePool);
  const eventName = useSetting('event_name', eventInfo.name);

  const loadStats = useCallback(async () => {
    try {
      const { count: domainCount } = await supabase.from('domains').select('*', { count: 'exact', head: true }).eq('status', true);
      setTotalDomains(domainCount || 0);
    } catch { /* use default */ }
    try {
      const { data: prizeData } = await supabase.from('settings').select('value').eq('key', 'prize_display_text').maybeSingle();
      if (prizeData?.value) setPrizePool(prizeData.value);
    } catch { /* use default */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useRealtime({ table: 'domains', onAny: () => loadStats() });
  useRealtime({ table: 'settings', onAny: () => loadStats() });

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Abstract%20medical%20technology%20background%20with%20soft%20geometric%20hexagon%20patterns%20in%20teal%20and%20white%2C%20clean%20minimalist%20medical%20aesthetic%20with%20subtle%20digital%20grid%20overlay%2C%20professional%20healthcare%20innovation%20theme%2C%20light%20airy%20atmosphere%20with%20gentle%20gradients%2C%20no%20text%20or%20people&width=1800&height=1200&seq=sona-clinnova-hero-bg&orientation=landscape"
        alt={`${eventName} Medical Innovation Hackathon`}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/50 to-background-950/80"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
          <i className="ri-calendar-event-line text-accent-400 text-sm"></i>
          <span className="text-white/90 text-sm font-medium">{eventInfo.date}</span>
          <span className="text-white/40">•</span>
          <span className="text-white/90 text-sm font-medium">{eventInfo.venue}</span>
        </div>

        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-4">
          {eventName}
        </h1>

        <p className="text-white/80 text-sm md:text-base mb-4 font-heading">
          Hosted by <strong>Sona College of Technology, Salem</strong>
        </p>
        <p className="text-white/70 text-xs md:text-sm mb-4">
          In association with <strong>Indian Medical Association (IMA), Salem</strong>
        </p>

        <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
          {eventInfo.description}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link
            to="/register"
            className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-accent-500 hover:bg-accent-600 text-background-50 dark:text-foreground-950 font-semibold text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Register Your Team
          </Link>
          <a
            href="#about"
            className="w-full sm:w-auto px-8 py-3.5 rounded-lg border border-white/30 hover:border-white/60 text-white font-medium text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Learn More
          </a>
        </div>

        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
          {[
            { icon: 'ri-stack-line', value: totalDomains.toString(), label: 'Total Domains' },
            { icon: 'ri-trophy-line', value: prizePool, label: 'Prize Pool' },
            { icon: 'ri-time-line', value: eventInfo.duration, label: 'Duration' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="w-9 h-9 mx-auto mb-2 rounded-lg bg-white/10 flex items-center justify-center">
                <i className={`${stat.icon} text-accent-400 text-base`}></i>
              </div>
              <div className="text-xl md:text-2xl font-heading font-bold text-white">{stat.value}</div>
              <div className="text-xs text-white/60 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}